// Calendar event builders for shows — Google Calendar URL and .ics download.
// The event repeats weekly on the show's days and times in America/New_York,
// with a 15-minute reminder.

const DAY_TO_ICAL = {
  monday: 'MO', tuesday: 'TU', wednesday: 'WE', thursday: 'TH',
  friday: 'FR', saturday: 'SA', sunday: 'SU',
};

const SHOW_URL_BASE = 'https://yakaleo.base44.app';
const LISTEN_URL = 'https://yakaleo.base44.app';

// Finds the next occurrence date (YYYYMMDD) of the show in ET.
function getNextDateET(show) {
  if (!show?.days?.length || !show.start_time) return null;
  const now = new Date();
  const [sh, sm] = show.start_time.split(':').map(Number);
  const startMin = sh * 60 + sm;

  for (let i = 0; i < 8; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);

    const weekday = parts.find((p) => p.type === 'weekday').value.toLowerCase();
    if (!show.days.includes(weekday)) continue;

    if (i === 0) {
      const hour = parseInt(parts.find((p) => p.type === 'hour').value, 10);
      const minute = parseInt(parts.find((p) => p.type === 'minute').value, 10);
      if (hour * 60 + minute >= startMin) continue;
    }

    const y = parts.find((p) => p.type === 'year').value;
    const m = parts.find((p) => p.type === 'month').value;
    const day = parts.find((p) => p.type === 'day').value;
    return `${y}${m}${day}`;
  }
  return null;
}

function buildDescription(show, lang, hostName) {
  const es = lang === 'es';
  const showUrl = show.slug
    ? `${SHOW_URL_BASE}/${es ? 'programa' : 'en/show'}/${show.slug}`
    : SHOW_URL_BASE;
  const parts = [];
  if (hostName) parts.push(es ? `Con ${hostName}` : `With ${hostName}`);
  parts.push(`${es ? 'Programa' : 'Show'}: ${showUrl}`);
  parts.push(`${es ? 'Escucha en vivo' : 'Listen live'}: ${LISTEN_URL}`);
  return parts;
}

export function googleCalendarUrl(show, lang, hostName) {
  const date = getNextDateET(show);
  if (!date) return null;
  const [sh, sm] = (show.start_time || '00:00').split(':').map(Number);
  const [eh, em] = (show.end_time || '00:00').split(':').map(Number);
  const start = `${date}T${String(sh).padStart(2, '0')}${String(sm).padStart(2, '0')}00`;
  const end = `${date}T${String(eh).padStart(2, '0')}${String(em).padStart(2, '0')}00`;
  const byDays = (show.days || []).map((d) => DAY_TO_ICAL[d]).join(',');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${show.name} on Yakaleo`,
    dates: `${start}/${end}`,
    ctz: 'America/New_York',
    recur: `RRULE:FREQ=WEEKLY;BYDAY=${byDays}`,
    details: buildDescription(show, lang, hostName).join('\n'),
    location: LISTEN_URL,
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}

export function buildICS(show, lang, hostName) {
  const date = getNextDateET(show);
  if (!date) return null;
  const [sh, sm] = (show.start_time || '00:00').split(':').map(Number);
  const [eh, em] = (show.end_time || '00:00').split(':').map(Number);
  const start = `${date}T${String(sh).padStart(2, '0')}${String(sm).padStart(2, '0')}00`;
  const end = `${date}T${String(eh).padStart(2, '0')}${String(em).padStart(2, '0')}00`;
  const byDays = (show.days || []).map((d) => DAY_TO_ICAL[d]).join(',');
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const desc = buildDescription(show, lang, hostName).join('\\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Yakaleo//Show Calendar//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${show.slug || show.id || 'show'}@yakaleo`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=America/New_York:${start}`,
    `DTEND;TZID=America/New_York:${end}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${byDays}`,
    `SUMMARY:${show.name} on Yakaleo`,
    `DESCRIPTION:${desc}`,
    `LOCATION:${LISTEN_URL}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${show.name}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}